import { useState, useEffect } from 'react';
import { Undo2, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useListsStore } from '@/store/listsStore';
import { cn } from '@/utils/cn';

interface UndoButtonProps {
  className?: string;
  autoHide?: boolean;
  autoHideDelay?: number;
}

export function UndoButton({ 
  className, 
  autoHide = true, 
  autoHideDelay = 5000 
}: UndoButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isPerformingUndo, setIsPerformingUndo] = useState(false);
  
  const { 
    canUndo, 
    getLastUndoAction, 
    performUndo 
  } = useListsStore();

  const lastAction = getLastUndoAction();
  const showUndo = canUndo() && lastAction;

  useEffect(() => {
    if (showUndo) {
      setIsVisible(true);
      
      if (autoHide) {
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, autoHideDelay);
        
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
    
    return undefined;
  }, [showUndo, autoHide, autoHideDelay]);

  const handleUndo = async () => {
    if (!canUndo() || isPerformingUndo) return;
    
    setIsPerformingUndo(true);
    
    try {
      await performUndo();
      setIsVisible(false);
    } catch (error) {
      console.error('Failed to undo action:', error);
    } finally {
      setIsPerformingUndo(false);
    }
  };

  if (!isVisible || !showUndo) {
    return null;
  }

  return (
    <div className={cn(
      'fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50',
      'animate-in slide-in-from-bottom-5 duration-300',
      className
    )}>
      <div className="bg-gray-800 text-white rounded-lg shadow-lg p-3 flex items-center gap-3 min-w-[280px] max-w-[400px]">
        <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {lastAction?.description || 'Action completed'}
          </p>
          <p className="text-xs text-gray-300">
            {lastAction && new Intl.DateTimeFormat('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            }).format(lastAction.timestamp)}
          </p>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleUndo}
          disabled={isPerformingUndo}
          className="text-white hover:bg-gray-700 flex-shrink-0"
        >
          <Undo2 className={cn(
            'h-4 w-4 mr-1',
            isPerformingUndo && 'animate-spin'
          )} />
          Undo
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:bg-gray-700 flex-shrink-0 px-2"
        >
          ×
        </Button>
      </div>
    </div>
  );
}

// Hook for triggering undo notifications
export function useUndoNotification() {
  const { addToUndoStack } = useListsStore();
  
  const addUndoAction = (action: {
    type: 'item_complete' | 'item_delete' | 'item_move' | 'bulk_operation';
    listId: string;
    description: string;
    data: any;
  }) => {
    addToUndoStack({
      id: `undo_${Date.now()}`,
      timestamp: new Date(),
      canUndo: true,
      ...action
    });
  };
  
  return { addUndoAction };
}